import type { ChildrenOnly } from '@studio/design-system/props';
import { Button } from '@studio/design-system/button';
import type { Room } from '@studio/models/room';
import { useEffect, useLayoutEffect, useState } from 'react';
import { resolveInitialRoomBilling } from '#~/api/agent/registry.js';
import { initialBillingIsPending } from '#~/api/agent/initial-billing.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { OAuthTimeout } from '#~/auth/constants.js';
import { TimeoutError, withTimeout } from '#~/lib/with-timeout.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { INITIAL_BOOT_OBSERVATION } from '#~/depot/types.js';

/** Check initial setup before providers connect a newly created Studio.
 * Known pending survives transport errors; ordinary unknown billing remains permissive.
 * Per absent-data-gates-permissively (ai.knowledge/global), billing errors preserve ordinary access;
 * the separate auth deadline keeps token retrieval recoverable. */
export const InitialBillingGate = ({ roomId, children }: ChildrenOnly & { roomId: Room['id'] }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [attempt, setAttempt] = useState(0);
  const [classification, setClassification] = useState<{
    roomId: Room['id'];
    status: 'checking' | 'pending' | 'retry' | 'ready';
  }>({ roomId, status: 'checking' });
  // A ready result belongs to one Studio. Route changes cannot admit the next
  // Studio before its effect has started the primary check.
  const state = classification.roomId === roomId ? classification.status : 'checking';
  const needsRetry = state === 'pending' || state === 'retry';
  const produce = useDepot((store) => store.produce);
  useLayoutEffect(() => {
    if (state === 'ready') return;
    // A retry screen is visible final UI, not a connected Studio. Dismiss the
    // boot overlay so its button is usable without publishing Studio success.
    produce((draft) => {
      draft.boot.isRootReady = needsRetry;
      draft.boot.observation = {
        ...(draft.boot.observation ?? INITIAL_BOOT_OBSERVATION),
        outcome:
          draft.boot.observation?.outcome === 'failed'
            ? 'failed'
            : needsRetry
              ? 'awaiting_user'
              : 'pending',
        roomId,
        roomFacts: 'unknown'
      };
    });
  }, [produce, roomId, state, needsRetry]);
  useEffect(() => {
    let disposed = false;
    setClassification({ roomId, status: 'checking' });
    const check = async () => {
      let tokenReceived = false;
      try {
        // Per settle-every-terminal-branch (ai.knowledge/global), same-room Retry needs its own deadline.
        const token = await withTimeout(
          getAccessTokenSilently(),
          OAuthTimeout,
          'Initial Studio token'
        );
        tokenReceived = true;
        const pending = await resolveInitialRoomBilling(roomId, token);
        if (!disposed) setClassification({ roomId, status: pending ? 'pending' : 'ready' });
      } catch (cause) {
        // An unavailable request cannot erase a previously confirmed unfinished setup.
        if (!disposed)
          setClassification({
            roomId,
            // Auth recovery does not invent a pending billing decision.
            status:
              !tokenReceived && cause instanceof TimeoutError
                ? 'retry'
                : initialBillingIsPending(roomId)
                  ? 'pending'
                  : 'ready'
          });
      }
    };
    void check();
    return () => {
      disposed = true;
    };
  }, [roomId, attempt, getAccessTokenSilently]);

  if (state === 'ready') return <>{children}</>;
  return (
    <main
      className='grid min-h-screen place-items-center p-6'
      {...DataSTag(Sel.InitialBilling, 'setup')}
    >
      <div className='max-w-[24rem] text-center' role='status'>
        <p>
          {state === 'checking'
            ? 'Setting up your studio...'
            : 'Studio setup could not finish. Try again to continue.'}
        </p>
        {needsRetry && (
          <Button
            className='mx-auto mt-16 focus-visible:ring-2 focus-visible:ring-focus'
            {...STagNew(Sel.InitialBilling, 'retry')}
            onClick={() => setAttempt((value) => value + 1)}
          >
            Try again
          </Button>
        )}
      </div>
    </main>
  );
};
